import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { LenderRetailActivityLenderRowViewModel } from '../../../api/types';
import { formatCompactAmount, getCountryLabel, getFinancedBarColor, groupLenderRowsByCountry } from '../../../lib';
import { getCountryFlag } from './country';

interface Props {
  rows: LenderRetailActivityLenderRowViewModel[];
  loading?: boolean;
}

const CHART_HEIGHT = 280;
const CHART_MARGIN_LEFT = 160;
// Rough average glyph width (px) for the bar label font, used to decide if a label fits inside its bar
const BAR_LABEL_CHAR_WIDTH = 7;
const BAR_LABEL_PADDING = 8;
// Rough average glyph width (px) for the y-axis label font
const AXIS_LABEL_CHAR_WIDTH = 6.5;
// Space reserved for tick marks/padding between the label and the chart area
const AXIS_LABEL_PADDING = 12;

const getBarLabelForWidth = (value: number | null, barWidth: number): string => {
  if (value == null) return '';

  const text = formatCompactAmount(value);
  const estimatedTextWidth = text.length * BAR_LABEL_CHAR_WIDTH + BAR_LABEL_PADDING;

  // Hide the label when the bar is too small to fit it, so it doesn't overflow into the axis and get clipped
  return estimatedTextWidth <= barWidth ? text : '';
};

// Truncate the lender name from the end (keeping the start visible) so it fits within the left margin
const truncateLenderName = (name: string): string => {
  const maxWidth = CHART_MARGIN_LEFT - AXIS_LABEL_PADDING;
  const maxChars = Math.floor(maxWidth / AXIS_LABEL_CHAR_WIDTH);

  if (name.length <= maxChars) return name;
  if (maxChars <= 1) return '…';

  return `${name.slice(0, maxChars - 1).trimEnd()}…`;
};

export const RequestedFinancing = ({ rows, loading }: Props) => {
  const groups = useMemo(
    () =>
      groupLenderRowsByCountry(rows)
        .map(group => ({
          ...group,
          rows: group.rows.filter(row => row.requestedAmount > 0).sort((a, b) => b.requestedAmount - a.requestedAmount),
        }))
        .filter(group => group.rows.length > 0),
    [rows]
  );

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2" component="span">
        Requested financing ($)
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        {loading ? (
          <Skeleton variant="rounded" height={CHART_HEIGHT} />
        ) : !groups.length ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            No data
          </Typography>
        ) : (
          <Stack gap={3}>
            {groups.map(group => {
              const flag = getCountryFlag(group.country);

              return (
                <Box key={group.country}>
                  <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                    {flag && <Box component={flag} sx={{ width: 16, height: 16 }} />}
                    <Typography variant="subtitle2" component="span">
                      {getCountryLabel(group.country)}
                    </Typography>
                  </Stack>

                  <BarChart
                    layout="horizontal"
                    height={Math.max(group.rows.length * 40 + 40, 120)}
                    margin={{ left: CHART_MARGIN_LEFT, top: 10, right: 40, bottom: 24 }}
                    yAxis={[
                      {
                        scaleType: 'band',
                        // Key by row index so duplicate lender names don't collapse onto the same band
                        data: group.rows.map((_, index) => index),
                        valueFormatter: (value, context) => {
                          const name = group.rows[Number(value)]?.lenderName ?? '';
                          return context.location === 'tick' ? truncateLenderName(name) : name;
                        },
                      },
                    ]}
                    xAxis={[{ min: 0, valueFormatter: value => formatCompactAmount(Number(value)) }]}
                    series={[
                      {
                        data: group.rows.map(row => row.requestedAmount),
                        color: getFinancedBarColor(group.country),
                        valueFormatter: value => (value == null ? '' : formatCompactAmount(value)),
                      },
                    ]}
                    barLabel={(item, context) => getBarLabelForWidth(item.value, context.bar.width)}
                    slotProps={{ legend: { hidden: true } }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
